if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        if (this.hasAttribute("data-editorial-gallery")) {
          this.initEditorialGallery();
          return;
        }
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      initEditorialGallery() {
        this.variantMediaId = this.querySelector(".is-active[data-media-id]")?.dataset.mediaId;
        this.addEventListener("click", (event) => {
          const photo = event.target.closest("[data-gallery-photo]");
          if (photo) this.selectEditorialMedia(photo.dataset.galleryPhoto);
          const step = event.target.closest("[data-gallery-step]");
          if (step) this.stepEditorialMedia(Number(step.dataset.galleryStep));
          if (event.target.closest("[data-gallery-expand]")) {
            const slide = this.querySelector(".is-active[data-media-id]");
            const opener = event.target.closest("button");
            opener.dataset.mediaId = slide.dataset.photoId;
            document
              .getElementById(`ProductModal-${this.dataset.section}`)
              ?.show(opener);
          }
          const overview = event.target.closest("[data-gallery-overview]");
          if (overview) {
            const open = overview.getAttribute("aria-expanded") !== "true";
            overview.setAttribute("aria-expanded", String(open));
            this.querySelector(".cb-artwork__overview").hidden = !open;
          }
        });
        this.addEventListener("keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
            return;
          event.preventDefault();
          const slides = this.getEditorialSlides();
          if (!slides.length) return;
          if (event.key === "Home" || event.key === "End")
            this.selectEditorialMedia(
              slides[event.key === "Home" ? 0 : slides.length - 1].dataset
                .mediaId,
            );
          else this.stepEditorialMedia(event.key === "ArrowRight" ? 1 : -1);
        });
        const stage = this.querySelector(".cb-artwork__stage");
        stage.addEventListener(
          "touchstart",
          (event) => {
            this.touchStart = {
              x: event.touches[0].clientX,
              y: event.touches[0].clientY,
            };
          },
          { passive: true },
        );
        stage.addEventListener(
          "touchend",
          (event) => {
            if (!this.touchStart) return;
            const dx = event.changedTouches[0].clientX - this.touchStart.x;
            const dy = event.changedTouches[0].clientY - this.touchStart.y;
            if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
              this.stepEditorialMedia(dx < 0 ? 1 : -1);
              this.suppressOpenUntil = Date.now() + 400;
            }
            this.touchStart = null;
          },
          { passive: true },
        );
        stage.addEventListener("click", (event) => {
          if (Date.now() < (this.suppressOpenUntil || 0))
            event.stopPropagation();
        });
        this.selectEditorialMedia(
          this.querySelector(".is-active[data-media-id]")?.dataset.mediaId,
          false,
        );
      }

      stepEditorialMedia(delta) {
        const slides = this.getEditorialSlides();
        if (!slides.length) return;
        const index = slides.findIndex((slide) =>
          slide.classList.contains("is-active"),
        );
        this.selectEditorialMedia(
          slides[(index + delta + slides.length) % slides.length].dataset
            .mediaId,
        );
      }

      getEditorialSlides() {
        // Shared product photos remain available; other variants' bottle photos do not.
        return [...this.querySelectorAll("li[data-media-id]")].filter(
          (slide) => slide.dataset.variantPhoto !== "true" || slide.dataset.mediaId === this.variantMediaId,
        );
      }

      selectEditorialMedia(mediaId, announce = true) {
        const slides = this.getEditorialSlides();
        const active =
          slides.find((slide) => slide.dataset.mediaId === mediaId) ||
          slides[0];
        if (!active) return;
        this.querySelectorAll("li[data-media-id]").forEach((slide) => {
          slide.hidden = slide !== active;
          slide.classList.toggle("is-active", slide === active);
        });
        this.querySelector("[data-gallery-number]").textContent = String(
          slides.indexOf(active) + 1,
        ).padStart(2, "0");
        this.querySelector("[data-gallery-title]").textContent =
          active.dataset.photoTitle.replace(/ Concentration$/, " bottle");
        this.querySelectorAll("[data-gallery-photo]").forEach((button) => {
          const index = slides.findIndex((slide) => slide.dataset.mediaId === button.dataset.galleryPhoto);
          button.hidden = index < 0;
          if (index >= 0) {
            button.setAttribute("aria-label", `View product photo ${index + 1} from overview`);
            button.querySelector("span").textContent = String(index + 1).padStart(2, "0");
          }
          if (button.dataset.galleryPhoto === active.dataset.mediaId)
            button.setAttribute("aria-current", "true");
          else button.removeAttribute("aria-current");
        });
        const singlePhoto = slides.length < 2;
        this.querySelector(".cb-artwork__arrows").hidden = singlePhoto;
        this.querySelector("[data-gallery-number]").hidden = singlePhoto;
        this.querySelector(".cb-artwork__stage").setAttribute("aria-label",
          singlePhoto ? "Product photo" : "Product photos. Use left and right arrow keys to browse.");
        this.querySelector(".cb-artwork__overview").hidden = true;
        this.querySelector("[data-gallery-overview]").setAttribute("aria-expanded", "false");
        if (announce)
          this.querySelector("[role=status]").textContent =
            `${active.dataset.photoTitle}. Photo ${slides.indexOf(active) + 1} of ${slides.length}.`;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.hasAttribute("data-editorial-gallery")) {
          this.variantMediaId = mediaId;
          this.selectEditorialMedia(mediaId);
          return;
        }
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (
            this.elements.thumbnails &&
            !this.classList.contains("cb-gallery")
          ) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (
            activeMediaRect.top > -0.5 ||
            (prepend && this.classList.contains("cb-gallery"))
          )
            return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
